import {
  ArrowRight,
  Check,
  FileCheck2,
  Info,
  LayoutGrid,
  PlayCircle,
  Sparkles,
  Upload,
} from 'lucide-react'
import React, {FC, ReactNode, useId, useLayoutEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'

import type {AllocationCounts} from '../../utils/teacherAllocationModel'
import {AllocationProgressBar, InfoIconTooltip} from './AllocationShared'
import {ProgressSkeleton} from './AllocationSkeleton'

type Props = {
  counts: AllocationCounts
  /** Assignments currently filled from files uploaded on Add Teachers (Layout 02 when > 0). */
  prefilledCount: number
  /** Format chips on the upload card. */
  uploadFormats: string[]
  uploadDisabled?: boolean
  /** Video guide URL; without one the "Watch a 2-minute guide" action is shown with no link. */
  guideUrl?: string
  onOpenGrid: () => void
  onOpenAi: () => void
  onUploadFiles: () => void
  /** The existing upload / pre-fill panels (file status, questions, reports). */
  importArea?: ReactNode
  /**
   * `ALLOCATION_UPLOADER_IN_CARD`: the Upload card's body, where the page puts the shared
   * uploader (drop zone, reading, a tick per file) in place of the format chips and the button.
   */
  uploadSlotRef?: (element: HTMLDivElement | null) => void
  footer: ReactNode
  /** Server flow: the progress is being read: the card shows a skeleton. */
  loading?: boolean
  /**
   * Server flow: the cards are selectable (click or keyboard) and highlight on hover or focus,
   * their button turning primary with them. Every button works at once — no card has to be
   * selected first. Nothing is selected on arrival.
   */
  selectableMethods?: boolean
}

type AllocationMethod = 'ai' | 'upload' | 'manual'

/**
 * Teacher Allocation landing page (handover, Points 1 and 2). One page, two states: Layout 01
 * with nothing pre-filled, Layout 02 when the Add Teachers files pre-filled some assignments.
 * Every count is derived from current data; the three methods share the same allocations.
 */
const AllocationLanding: FC<Props> = ({
  counts,
  prefilledCount,
  uploadFormats,
  uploadDisabled,
  guideUrl,
  onOpenGrid,
  onOpenAi,
  onUploadFiles,
  importArea,
  uploadSlotRef,
  footer,
  loading,
  selectableMethods,
}) => {
  const intl = useIntl()
  const groupName = useId()
  const [selected, setSelected] = useState<AllocationMethod | null>(null)
  const t = (id: string, values?: Record<string, ReactNode>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.TEACHER_ALLOCATION.${id}`}, values as any)
  const prefilled = prefilledCount > 0

  // The uploader in the Upload card (`uploadSlotRef`): the three cards are one height while it
  // shows its empty drop zone (the grid stretches them); once files are on it only the Upload
  // card grows — the AI and Manual cards keep the height they had (`restHeight`).
  const aiCardRef = useRef<HTMLElement>(null)
  const uploadCardRef = useRef<HTMLElement>(null)
  const [restHeight, setRestHeight] = useState<number | null>(null)
  const [uploadGrown, setUploadGrown] = useState(false)
  useLayoutEffect(() => {
    const aiCard = aiCardRef.current
    const uploadCard = uploadCardRef.current
    if (!uploadSlotRef || !aiCard || !uploadCard || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      const empty = Boolean(uploadCard.querySelector('.step4-te-dropzone'))
      setUploadGrown(!empty)
      if (empty) setRestHeight(aiCard.offsetHeight)
    }
    const observer = new ResizeObserver(measure)
    observer.observe(aiCard)
    observer.observe(uploadCard)
    measure()
    return () => observer.disconnect()
  }, [uploadSlotRef])
  const restStyle = uploadSlotRef && uploadGrown && restHeight ? {minHeight: restHeight} : undefined
  /** What the method offers, as short ticked points (only beside the uploader in the card). */
  const points = (ids: string[]) =>
    uploadSlotRef ? (
      <ul className='ta-method-card__points'>
        {ids.map((id) => (
          <li key={id}>
            <Check size={16} strokeWidth={2.5} aria-hidden />
            <span>{t(id)}</span>
          </li>
        ))}
      </ul>
    ) : null

  // Without selection (client flow) the AI card is featured, as designed. Selectable cards: the
  // selected one is primary; a hovered or focused one turns primary too (CSS).
  const isPrimary = (method: AllocationMethod) =>
    selectableMethods ? selected === method : method === 'ai'
  const buttonClass = (method: AllocationMethod) =>
    `ta-btn ${isPrimary(method) ? 'ta-btn--primary' : 'ta-btn--outline'} ta-btn--block`
  /** Selectable cards: on every card, shown with the highlight (CSS); else on the primary one. */
  const arrow = (method: AllocationMethod) =>
    selectableMethods || isPrimary(method) ? (
      <ArrowRight size={18} aria-hidden className='ta-method-card__arrow' />
    ) : null
  const cardProps = (method: AllocationMethod) => {
    if (!selectableMethods) {
      return {className: `ta-method-card${method === 'ai' ? ' ta-method-card--featured' : ''}`}
    }
    return {
      className: `ta-method-card ta-method-card--selectable${selected === method ? ' is-selected' : ''}`,
      onClick: () => setSelected(method),
    }
  }
  /** The card's own radio: keyboard selection (arrow keys) and its name for screen readers. */
  const radio = (method: AllocationMethod, label: string) =>
    selectableMethods ? (
      <input
        type='radio'
        className='ta-method-card__radio'
        name={groupName}
        value={method}
        checked={selected === method}
        aria-label={label}
        onChange={() => setSelected(method)}
      />
    ) : null

  return (
    <div className='ta-page ta-landing'>
      <header className='ta-page__header'>
        <h1 className='ta-page__title'>{t('LANDING.TITLE')}</h1>
        <p className='ta-page__subtitle'>
          {prefilled ? t('LANDING.SUBTITLE_PREFILLED') : t('LANDING.SUBTITLE')}
        </p>
      </header>

      <section className='ta-card ta-landing__progress' aria-live='polite'>
        <div className='ta-landing__progress-main'>
          {loading ? (
            <ProgressSkeleton />
          ) : (
            <>
              <div className='ta-landing__progress-title'>
                {prefilled || counts.filled > 0
                  ? t('PROGRESS.FILLED_OF', {filled: counts.filled, total: counts.total})
                  : t('PROGRESS.COMPLETED_OF', {filled: counts.filled, total: counts.total})}
              </div>
              <div className='ta-landing__progress-sub'>
                {t('PROGRESS.REMAINING', {count: counts.remaining})}
              </div>
              <AllocationProgressBar counts={counts} label={t('PROGRESS.ARIA')} />
            </>
          )}
          {prefilled && !loading && (
            <div className='ta-landing__prefill-note'>
              <FileCheck2 size={30} aria-hidden className='ta-landing__prefill-icon' />
              <p>
                {t('LANDING.PREFILL_NOTE', {count: prefilledCount, remaining: counts.remaining})}
              </p>
            </div>
          )}
        </div>
        <div className='ta-landing__progress-action'>
          <button type='button' className='ta-btn ta-btn--outline ta-btn--lg' onClick={onOpenGrid}>
            <LayoutGrid size={20} aria-hidden />
            {prefilled ? t('LANDING.REVIEW_GRID') : t('LANDING.VIEW_GRID')}
          </button>
        </div>
      </section>

      <section className='ta-landing__methods'>
        <div className='ta-landing__methods-head'>
          <div>
            <h2 className='ta-section-title'>{t('LANDING.METHODS_TITLE')}</h2>
            <p className='ta-section-subtitle'>{t('LANDING.METHODS_SUBTITLE')}</p>
          </div>
          {guideUrl ? (
            <a
              className='ta-link ta-landing__guide'
              href={guideUrl}
              target='_blank'
              rel='noreferrer'
            >
              <PlayCircle size={26} aria-hidden />
              {t('LANDING.GUIDE')}
            </a>
          ) : (
            // No video yet: shown as designed; clicking says it is coming soon.
            <button
              type='button'
              className='ta-link ta-landing__guide'
              onClick={() =>
                toast.info(t('LANDING.GUIDE_COMING_SOON'), {toastId: 'ta-guide-coming-soon'})
              }
            >
              <PlayCircle size={26} aria-hidden />
              {t('LANDING.GUIDE')}
            </button>
          )}
        </div>

        <div
          className='ta-landing__cards'
          role={selectableMethods ? 'radiogroup' : undefined}
          aria-label={selectableMethods ? t('LANDING.METHODS_TITLE') : undefined}
        >
          <article {...cardProps('ai')} ref={aiCardRef} style={restStyle}>
            {radio('ai', t('LANDING.AI_TITLE'))}
            <div className='ta-method-card__top'>
              <span className='ta-method-card__icon'>
                <Sparkles size={24} aria-hidden />
              </span>
              <InfoIconTooltip text={t('TOOLTIP.AI')} label={t('LANDING.AI_TITLE')} />
            </div>
            <h3 className='ta-method-card__title'>{t('LANDING.AI_TITLE')}</h3>
            <p className='ta-method-card__text'>{t('LANDING.AI_TEXT')}</p>
            {points([
              'LANDING.AI_POINT_MANY',
              'LANDING.AI_POINT_PREVIEW',
              'LANDING.AI_POINT_CLARIFY',
            ])}
            <div className='ta-method-card__example'>{t('LANDING.AI_EXAMPLE')}</div>
            <button type='button' className={buttonClass('ai')} onClick={onOpenAi}>
              {t('LANDING.AI_CTA')}
              {arrow('ai')}
            </button>
          </article>

          <article {...cardProps('upload')} ref={uploadCardRef}>
            {radio('upload', t('LANDING.UPLOAD_TITLE'))}
            <div className='ta-method-card__top'>
              <span className='ta-method-card__icon'>
                <Upload size={24} aria-hidden />
              </span>
              <InfoIconTooltip text={t('TOOLTIP.UPLOAD')} label={t('LANDING.UPLOAD_TITLE')} />
            </div>
            <h3 className='ta-method-card__title'>{t('LANDING.UPLOAD_TITLE')}</h3>
            <p className='ta-method-card__text'>{t('LANDING.UPLOAD_TEXT')}</p>
            {uploadSlotRef ? (
              // The uploader is the card's body, as on Add Teachers; its button opens the picker.
              <div ref={uploadSlotRef} className='ta-method-card__uploader' />
            ) : (
              <>
                <div className='ta-method-card__chips'>
                  {uploadFormats.map((format) => (
                    <span key={format} className='ta-chip'>
                      {format}
                    </span>
                  ))}
                </div>
                <button
                  type='button'
                  className={buttonClass('upload')}
                  onClick={onUploadFiles}
                  disabled={uploadDisabled}
                >
                  {t('LANDING.UPLOAD_CTA')}
                  {arrow('upload')}
                </button>
              </>
            )}
          </article>

          <article {...cardProps('manual')} style={restStyle}>
            {radio('manual', t('LANDING.MANUAL_TITLE'))}
            <div className='ta-method-card__top'>
              <span className='ta-method-card__icon'>
                <LayoutGrid size={24} aria-hidden />
              </span>
              <InfoIconTooltip text={t('TOOLTIP.MANUAL')} label={t('LANDING.MANUAL_TITLE')} />
            </div>
            <h3 className='ta-method-card__title'>{t('LANDING.MANUAL_TITLE')}</h3>
            <p className='ta-method-card__text'>{t('LANDING.MANUAL_TEXT')}</p>
            {points([
              'LANDING.MANUAL_POINT_PICK',
              'LANDING.MANUAL_POINT_FILTER',
              'LANDING.MANUAL_POINT_SETTINGS',
            ])}
            <div className='ta-method-card__example'>{t('LANDING.MANUAL_NOTE')}</div>
            <button type='button' className={buttonClass('manual')} onClick={onOpenGrid}>
              {t('LANDING.MANUAL_CTA')}
              {arrow('manual')}
            </button>
          </article>
        </div>
      </section>

      {importArea}

      <p className='ta-landing__settings-note'>
        <Info size={16} aria-hidden />
        {t('LANDING.SETTINGS_NOTE')}
      </p>

      {footer}
    </div>
  )
}

export default AllocationLanding
